import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { contactLinksService } from '../services/contactLinksService';
import type { ContactLink } from '../types/contact';
import { renderContactIcon } from './admin/ContactLinksAdmin';
import { HiCheck, HiClipboardCopy, HiExternalLink } from 'react-icons/hi';

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 260, damping: 20 },
  },
};

export default function Contact() {
  const [links, setLinks] = useState<ContactLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    contactLinksService.getActive().then((data) => {
      if (mounted) {
        setLinks(data);
        setLoading(false);
      }
    });

    const unsubscribe = contactLinksService.subscribe(() => {
      contactLinksService.getActive().then((data) => {
        if (mounted) setLinks(data);
      });
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleCopy = (e: React.MouseEvent, id: string, text: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-16 pt-28"
      dir="rtl"
    >
      <Helmet>
        <title>تماس با ما | Active Legend - راه‌های ارتباط با استودیو</title>
        <meta
          name="description"
          content="راه‌های رسمی ارتباط با استودیو بازی‌سازی اکتیو لجند (Active Legend). پشتیبانی، تلگرام، اینستاگرام، دیسکورد و ایمیل رسمی."
        />
        <link rel="canonical" href="https://activelegend.ir/contact" />
        <meta property="og:title" content="تماس با ما | Active Legend" />
        <meta
          property="og:description"
          content="راه‌های ارتباط با استودیو بازی‌سازی اکتیو لجند و تیم توسعه‌دهنده."
        />
        <meta property="og:url" content="https://activelegend.ir/contact" />
        <meta property="og:image" content="https://activelegend.ir/AE%20logo.svg" />
      </Helmet>

      <motion.div
        className="w-full max-w-2xl bg-zinc-900/80 rounded-3xl shadow-2xl p-6 sm:p-10 md:p-12 backdrop-blur-xl border border-white/15 text-center"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div variants={itemVariants} className="mb-2">
          <span className="inline-block px-4 py-1 bg-primary/10 border border-primary/30 rounded-full text-primary text-xs font-bold tracking-wider">
            پل‌های ارتباطی رسمی
          </span>
        </motion.div>

        <motion.h1
          className="text-3xl md:text-4xl font-black text-white mb-4"
          variants={itemVariants}
        >
          تماس با استودیو اکتیو لجند
        </motion.h1>

        <motion.p
          className="text-gray-300 mb-8 text-sm md:text-base leading-relaxed max-w-lg mx-auto"
          variants={itemVariants}
        >
          ما همیشه مشتاق شنیدن نظرات، پیشنهادات و همکاری با بازیکنان و بازی‌سازان هستیم. از طریق راه‌های زیر با ما در ارتباط باشید.
        </motion.p>

        {loading ? (
          <div className="py-12 flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <span className="text-gray-400 text-xs">در حال بارگذاری راه‌های ارتباطی...</span>
          </div>
        ) : links.length === 0 ? (
          <div className="py-8 text-gray-400 text-sm">
            در حال حاضر لینکی برای نمایش ثبت نشده است.
          </div>
        ) : (
          <div className="flex flex-col gap-4 mt-6">
            {links.map((link) => {
              const isHovered = hovered === link.id;
              const isCopied = copiedId === link.id;

              return (
                <motion.div
                  key={link.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.015 }}
                  whileTap={{ scale: 0.99 }}
                  onMouseEnter={() => setHovered(link.id)}
                  onMouseLeave={() => setHovered(null)}
                  className={`group relative flex items-center justify-between gap-4 p-4 sm:p-5 rounded-2xl border transition-all duration-300 ${
                    isHovered
                      ? 'bg-zinc-800/90 border-primary/40 shadow-xl shadow-primary/5'
                      : 'bg-black/50 border-white/10 hover:border-white/20'
                  }`}
                >
                  {/* Link Icon & Details */}
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 flex-1 min-w-0 text-right"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-zinc-800/90 border border-white/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                      {renderContactIcon(link.icon, 'w-6 h-6')}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="text-base sm:text-lg font-bold text-white group-hover:text-primary transition-colors truncate">
                        {link.label}
                      </div>
                      <div
                        className="text-xs sm:text-sm text-gray-400 font-mono truncate"
                        dir="ltr"
                      >
                        {link.value}
                      </div>
                    </div>
                  </a>

                  {/* Actions: Direct link & Copy Handle */}
                  <div className="flex items-center gap-2 shrink-0">
                    {link.value && (
                      <button
                        type="button"
                        onClick={(e) => handleCopy(e, link.id, link.value)}
                        title="کپی شناسه"
                        className={`p-2.5 rounded-xl border text-xs flex items-center gap-1.5 transition-all ${
                          isCopied
                            ? 'bg-emerald-950/80 border-emerald-500/40 text-emerald-300'
                            : 'bg-white/5 border-white/10 hover:bg-white/10 text-gray-300 hover:text-white'
                        }`}
                      >
                        {isCopied ? (
                          <>
                            <HiCheck className="w-4 h-4 text-emerald-400" />
                            <span className="text-[11px] font-bold">کپی شد</span>
                          </>
                        ) : (
                          <HiClipboardCopy className="w-4 h-4" />
                        )}
                      </button>
                    )}

                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="باز کردن پیوند"
                      className="p-2.5 rounded-xl bg-primary/10 hover:bg-primary text-primary hover:text-black border border-primary/20 transition-all"
                    >
                      <HiExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}
